import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";

import { isAuthenticatedAtom } from "../atoms/auth.atoms";
import authService from "../services/authService";

const SESSION_CHECK_INTERVAL_MS = 60_000; // 1 minute

/**
 * Periodically validates the current session against the backend by hitting
 * /auth/me. Returns `true` once the session has expired (a 401 surfaces via
 * the auth:session-expired event, or `validateToken` returns false).
 *
 * Migrated off `localStorage.@Eriol/system/accessKey` to use the bootstrap
 * atom so the validator also runs in browser context (where the HttpOnly
 * cookie carries the session and JS can't see it).
 */
export function useSessionValidator() {
  const [sessionExpired, setSessionExpired] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  const checkSession = useCallback(async () => {
    const valid = await authService.validateToken();
    if (!valid) setSessionExpired(true);
  }, []);

  // Periodic polling — only while authenticated
  useEffect(() => {
    if (!isAuthenticated) return;

    intervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, checkSession]);

  // Reset expired state when the user logs out (bootstrap transitions to
  // unauthenticated, or auth-changed event fires).
  useEffect(() => {
    if (!isAuthenticated) setSessionExpired(false);
  }, [isAuthenticated]);

  // Also listen for the auth:session-expired event from api.ts (immediate
  // signal on a 401 — faster than waiting for the next poll). Only while
  // authenticated: if the app boots without a session, a 401 from the
  // bootstrap /auth/me would otherwise stack the modal on top of the login
  // page.
  useEffect(() => {
    if (!isAuthenticated) return;
    const handler = () => setSessionExpired(true);
    window.addEventListener("auth:session-expired", handler);
    return () => window.removeEventListener("auth:session-expired", handler);
  }, [isAuthenticated]);

  return sessionExpired;
}
